import { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ListChecks,
  Plus,
  RefreshCw,
  Play,
  Copy,
  ChevronRight,
  MessageSquare,
  Trash2,
  Edit2,
  User,
  Bot,
  Check,
  Archive,
  ArchiveRestore,
  ArrowLeftRight,
  Sparkles,
} from "lucide-react";
import {
  testSuitesListTestSuites,
  testSuitesCreateTestSuite,
  testSuitesGetTestSuite,
  testSuitesCreateTestCase,
  testSuitesUpdateTestCase,
  testSuitesDeleteTestCase,
  testSuitesCloneTestSuite,
  testSuitesArchiveTestSuite,
  testSuitesUnarchiveTestSuite,
  environmentSnapshotsListEnvironmentSnapshots,
  testRunsCreateTestRun,
  testSuitesSuggestTestCaseTurn,
  testSuitesSuggestTestCaseTitle,
} from "../../gen";
import type {
  TestSuiteSummaryResponse,
  TestSuiteDetailResponse,
  TestCaseResponse,
  SnapshotResponse,
  TestSuiteListResponse,
  SnapshotListResponse,
  TestRunResponse,
} from "../../gen/models";
import { useOrg } from "../../hooks/useOrg";
import { useToast } from "../../context/ToastContext";
import { useUserDirectory } from "../../hooks/useUserDirectory";

interface TurnFormState {
  role: "user" | "assistant";
  content: string;
  generating?: boolean;
}

const AGENT_OPTIONS = [
  {
    value: "frontdesk",
    label: "Sofia (Frontdesk Concierge)",
    badge: "Sofia",
    color:
      "bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border border-purple-200 dark:border-purple-800",
  },
  {
    value: "sales",
    label: "Don Silvestre (Sales & Quotes)",
    badge: "Quotes",
    color:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800",
  },
  {
    value: "biblioteca",
    label: "La Biblioteca (Analytics)",
    badge: "Analytics",
    color:
      "bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800",
  },
  {
    value: "feedback",
    label: "El Notario (Feedback Curation)",
    badge: "Feedback",
    color:
      "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-800",
  },
  {
    value: "email_drafting",
    label: "Email Drafter (HubSpot Comms)",
    badge: "Email Drafter",
    color:
      "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300 border border-sky-200 dark:border-sky-800",
  },
];

function getAgentLabel(agentType?: string | null): string {
  const match = AGENT_OPTIONS.find((a) => a.value === agentType);
  return match ? match.badge : agentType || "Sofia";
}

function getAgentBadgeClass(agentType?: string | null): string {
  const match = AGENT_OPTIONS.find((a) => a.value === agentType);
  return match
    ? match.color
    : "bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-300";
}

interface AutoResizeTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  value: string;
}

function AutoResizeTextarea({
  value,
  onChange,
  className = "",
  rows = 2,
  ...props
}: AutoResizeTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const newHeight = Math.max(el.scrollHeight, 44);
    el.style.height = `${newHeight}px`;
  }, []);

  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  useEffect(() => {
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [resize]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={(e) => {
        onChange?.(e);
        resize();
      }}
      onInput={resize}
      rows={rows}
      className={`overflow-hidden resize-none [field-sizing:content] ${className}`}
      {...props}
    />
  );
}

export function TestSuitesWorkspace() {
  const { suite_id, case_id } = useParams<{
    suite_id?: string;
    case_id?: string;
  }>();
  const { activeOrgId } = useOrg();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getAgentDisplayName } = useUserDirectory();

  const [suites, setSuites] = useState<TestSuiteSummaryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [suiteDetail, setSuiteDetail] =
    useState<TestSuiteDetailResponse | null>(null);
  const [agentFilter, setAgentFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // New Suite Modal
  const [isSuiteModalOpen, setIsSuiteModalOpen] = useState(false);
  const [suiteName, setSuiteName] = useState("");
  const [suiteDesc, setSuiteDesc] = useState("");
  const [suiteAgentType, setSuiteAgentType] = useState("frontdesk");
  const [creatingSuite, setCreatingSuite] = useState(false);

  // Selected or New Test Case Editor State
  const [isCreatingNewCase, setIsCreatingNewCase] = useState(false);
  const [isEditingCase, setIsEditingCase] = useState(false);
  const [caseTitle, setCaseTitle] = useState("");
  const [caseTurns, setCaseTurns] = useState<TurnFormState[]>([
    { role: "user", content: "" },
  ]);
  const [savingCase, setSavingCase] = useState(false);
  const [suggestingTurn, setSuggestingTurn] = useState(false);
  const [suggestingTitle, setSuggestingTitle] = useState(false);

  // Delete Case confirmation
  const [deletingCaseId, setDeletingCaseId] = useState<string | null>(null);

  // Copy turn feedback state
  const [copiedTurnIdx, setCopiedTurnIdx] = useState<number | null>(null);

  const handleCopyTurn = (idx: number, text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedTurnIdx(idx);
    setTimeout(() => setCopiedTurnIdx(null), 1500);
  };

  // Run Trigger Modal
  const [isRunModalOpen, setIsRunModalOpen] = useState(false);
  const [snapshots, setSnapshots] = useState<SnapshotResponse[]>([]);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState("");
  const [triggeringRun, setTriggeringRun] = useState(false);

  const selectedCase =
    suiteDetail?.test_cases.find((c) => c.id === case_id) || null;

  const fetchSuites = async (
    selectSuiteId?: string,
    overrideFilter?: string,
    overrideShowArchived?: boolean,
  ) => {
    setLoading(true);
    try {
      const activeFilter =
        overrideFilter !== undefined ? overrideFilter : agentFilter;
      const activeArchived =
        overrideShowArchived !== undefined ? overrideShowArchived : showArchived;
      const resp = await testSuitesListTestSuites({
        limit: 100,
        agent_type: activeFilter !== "all" ? activeFilter : undefined,
        include_archived: activeArchived,
      });
      const data = (resp as any)?.data ?? resp;
      if (data && "suites" in data) {
        const listResp = data as TestSuiteListResponse;
        setSuites(listResp.suites);

        // If no suite_id in URL and we have suites, navigate to the first suite
        if (!suite_id && !selectSuiteId && listResp.suites.length > 0) {
          navigate(`/test-suites/${listResp.suites[0].id}`, { replace: true });
        } else if (selectSuiteId) {
          navigate(`/test-suites/${selectSuiteId}`);
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Unknown error", "Failed to load test suites");
    } finally {
      setLoading(false);
    }
  };

  const fetchSuiteDetail = async (id: string) => {
    try {
      const resp = await testSuitesGetTestSuite(id);
      const data = (resp as any)?.data ?? resp;
      if (data && "id" in data) {
        setSuiteDetail(data as TestSuiteDetailResponse);
      }
    } catch (err: any) {
      toast.error(
        err.message || "Unknown error",
        "Failed to load suite details",
      );
    }
  };

  useEffect(() => {
    fetchSuites(undefined, undefined, showArchived);
  }, [activeOrgId, showArchived]);

  useEffect(() => {
    if (suite_id) {
      fetchSuiteDetail(suite_id);
    } else {
      setSuiteDetail(null);
    }
  }, [suite_id]);

  // Sync editor form when selectedCase changes or creation mode changes
  useEffect(() => {
    if (isCreatingNewCase) {
      setCaseTitle("");
      setCaseTurns([{ role: "user", content: "" }]);
      setIsEditingCase(true);
      return;
    }

    if (selectedCase) {
      setCaseTitle(selectedCase.title);
      const parsedTurns: TurnFormState[] =
        Array.isArray(selectedCase.script_turns) &&
        selectedCase.script_turns.length > 0
          ? selectedCase.script_turns.map((t: any) => ({
              role: (t.role === "assistant" ? "assistant" : "user") as
                | "user"
                | "assistant",
              content: typeof t.content === "string" ? t.content : "",
            }))
          : [{ role: "user", content: "" }];
      setCaseTurns(parsedTurns);
      setIsEditingCase(false);
    } else {
      setCaseTitle("");
      setCaseTurns([{ role: "user", content: "" }]);
      setIsEditingCase(false);
    }
  }, [selectedCase, isCreatingNewCase]);

  const handleSelectSuite = (id: string) => {
    setIsCreatingNewCase(false);
    navigate(`/test-suites/${id}`);
  };

  const handleSelectCase = (targetCaseId: string) => {
    if (!suite_id) return;
    setIsCreatingNewCase(false);
    navigate(`/test-suites/${suite_id}/cases/${targetCaseId}`);
  };

  const handleStartCreateCase = () => {
    setIsCreatingNewCase(true);
    setCaseTitle("");
    setCaseTurns([{ role: "user", content: "" }]);
    setIsEditingCase(true);
  };

  const handleCancelEditOrNew = () => {
    if (isCreatingNewCase) {
      setIsCreatingNewCase(false);
      if (selectedCase) {
        setIsEditingCase(false);
      }
    } else {
      // Revert edit
      if (selectedCase) {
        setCaseTitle(selectedCase.title);
        const parsedTurns: TurnFormState[] =
          Array.isArray(selectedCase.script_turns) &&
          selectedCase.script_turns.length > 0
            ? selectedCase.script_turns.map((t: any) => ({
                role: (t.role === "assistant" ? "assistant" : "user") as
                  | "user"
                  | "assistant",
                content: typeof t.content === "string" ? t.content : "",
              }))
            : [{ role: "user", content: "" }];
        setCaseTurns(parsedTurns);
      }
      setIsEditingCase(false);
    }
  };

  const handleCreateSuite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!suiteName.trim()) return;

    setCreatingSuite(true);
    try {
      const resp = await testSuitesCreateTestSuite({
        name: suiteName.trim(),
        agent_type: suiteAgentType,
        description: suiteDesc.trim() || undefined,
      });
      const data = (resp as any)?.data ?? resp;
      if (data && "id" in data) {
        const created = data as TestSuiteDetailResponse;
        toast.success(
          `Test suite '${created.name}' created for ${getAgentLabel(created.agent_type)}.`,
          "Suite created",
        );
        setIsSuiteModalOpen(false);
        setSuiteName("");
        setSuiteDesc("");
        setSuiteAgentType("frontdesk");
        await fetchSuites(created.id);
      }
    } catch (err: any) {
      toast.error(err.message, "Error creating suite");
    } finally {
      setCreatingSuite(false);
    }
  };

  const handleCloneSuite = async (id: string, name: string) => {
    try {
      const resp = await testSuitesCloneTestSuite(id, {
        new_name: `${name} (Copy)`,
      });
      const data = (resp as any)?.data ?? resp;
      if (data && "id" in data) {
        const cloned = data as TestSuiteDetailResponse;
        toast.success(`Cloned suite '${cloned.name}'`, "Cloned");
        await fetchSuites(cloned.id);
      }
    } catch (err: any) {
      toast.error(err.message, "Clone failed");
    }
  };

  const handleToggleArchiveSuite = async (suite: TestSuiteDetailResponse) => {
    setActionLoading(true);
    try {
      if (suite.is_archived) {
        await testSuitesUnarchiveTestSuite(suite.id);
        toast.success(`Suite '${suite.name}' restored.`, "Test suite unarchived");
      } else {
        await testSuitesArchiveTestSuite(suite.id);
        toast.info(`Suite '${suite.name}' archived.`);
      }
      await fetchSuiteDetail(suite.id);
      await fetchSuites(suite.id);
    } catch (err: any) {
      toast.error(err.message || "Operation failed", "Error toggling suite archive state");
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddMessageTurn = (role?: "user" | "assistant") => {
    setCaseTurns((prev) => {
      const last = prev[prev.length - 1];
      const defaultRole = role ?? (last?.role === "user" ? "assistant" : "user");
      return [...prev, { role: defaultRole, content: "" }];
    });
  };

  const handleToggleTurnRole = (idx: number) => {
    setCaseTurns((prev) =>
      prev.map((turn, i) =>
        i === idx
          ? {
              ...turn,
              role: turn.role === "user" ? "assistant" : "user",
            }
          : turn,
      ),
    );
  };

  const handleDeleteTurn = (idx: number) => {
    setCaseTurns((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAutoSuggest = async () => {
    if (!suiteDetail || suggestingTurn) return;

    // Filter valid turns up to this point for conversation history
    const validTurns = caseTurns
      .filter((t) => !t.generating && t.content.trim().length > 0)
      .map((t) => ({ role: t.role, content: t.content.trim() }));

    if (validTurns.length === 0) {
      toast.error(
        "Please provide at least one message turn before generating an auto-suggested answer.",
        "Missing context",
      );
      return;
    }

    // Append an assistant placeholder turn with generating: true
    const newIdx = caseTurns.length;
    setCaseTurns((prev) => [
      ...prev,
      { role: "assistant", content: "", generating: true },
    ]);
    setSuggestingTurn(true);

    try {
      const resp = await testSuitesSuggestTestCaseTurn(suiteDetail.id, {
        script_turns: validTurns,
      });
      const data = (resp as any)?.data ?? resp;

      setCaseTurns((prev) =>
        prev.map((t, i) =>
          i === newIdx
            ? { role: "assistant", content: data.content || "", generating: false }
            : t,
        ),
      );

      // Auto-populate case title if currently empty
      if (!caseTitle.trim() && data.suggested_title) {
        setCaseTitle(data.suggested_title);
      }
    } catch (err: any) {
      // Remove newly appended placeholder turn on failure
      setCaseTurns((prev) => prev.filter((_, i) => i !== newIdx));
      toast.error(
        err.message || "Failed to generate suggested turn",
        "Auto-Suggest Failed",
      );
    } finally {
      setSuggestingTurn(false);
    }
  };

  const handleSuggestTitle = async () => {
    if (!suiteDetail || suggestingTitle) return;

    const validTurns = caseTurns
      .filter((t) => !t.generating && t.content.trim().length > 0)
      .map((t) => ({ role: t.role, content: t.content.trim() }));

    if (validTurns.length === 0) {
      toast.error(
        "Please provide at least one message turn before generating a title.",
        "Missing context",
      );
      return;
    }

    setSuggestingTitle(true);
    try {
      const resp = await testSuitesSuggestTestCaseTitle(suiteDetail.id, {
        script_turns: validTurns,
      });
      const data = (resp as any)?.data ?? resp;
      if (data.suggested_title) {
        setCaseTitle(data.suggested_title);
        toast.success(`Title suggested: "${data.suggested_title}"`);
      }
    } catch (err: any) {
      toast.error(
        err.message || "Failed to generate suggested title",
        "Title Suggestion Failed",
      );
    } finally {
      setSuggestingTitle(false);
    }
  };

  const handleSaveCase = async () => {
    if (suggestingTurn) {
      toast.error("Please wait for auto-suggest to complete before saving.", "Suggestion in progress");
      return;
    }

    const validTurns = caseTurns
      .filter((t) => !t.generating)
      .map((t) => ({ role: t.role, content: t.content.trim() }))
      .filter((t) => t.content.length > 0);

    if (!suite_id) return;
    if (!caseTitle.trim()) {
      toast.error(
        "Please enter a test case title/scenario name.",
        "Missing title",
      );
      return;
    }
    if (validTurns.length === 0) {
      toast.error(
        "Please provide at least one conversation turn.",
        "Missing turns",
      );
      return;
    }

    setSavingCase(true);
    try {
      if (selectedCase && !isCreatingNewCase) {
        // Update case
        const resp = await testSuitesUpdateTestCase(suite_id, selectedCase.id, {
          title: caseTitle.trim(),
          script_turns: validTurns,
        });
        const data = (resp as any)?.data ?? resp;
        if (data && "id" in data) {
          const updated = data as TestCaseResponse;
          toast.success(`Updated '${updated.title}'`, "Test case updated");
          setIsEditingCase(false);
          await fetchSuiteDetail(suite_id);
          await fetchSuites();
        }
      } else {
        // Create case
        const resp = await testSuitesCreateTestCase(suite_id, {
          title: caseTitle.trim(),
          script_turns: validTurns,
        });
        const data = (resp as any)?.data ?? resp;
        if (data && "id" in data) {
          const created = data as TestCaseResponse;
          toast.success(`Added '${created.title}'`, "Test case added");
          setIsCreatingNewCase(false);
          setIsEditingCase(false);
          await fetchSuiteDetail(suite_id);
          await fetchSuites();
          navigate(`/test-suites/${suite_id}/cases/${created.id}`);
        }
      }
    } catch (err: any) {
      toast.error(
        err.message,
        selectedCase && !isCreatingNewCase
          ? "Error updating test case"
          : "Error creating test case",
      );
    } finally {
      setSavingCase(false);
    }
  };

  const handleDeleteCase = async (
    targetCaseId: string,
    e?: React.MouseEvent,
  ) => {
    if (e) e.stopPropagation();
    if (!suite_id) return;
    if (!window.confirm("Are you sure you want to delete this test case?"))
      return;

    setDeletingCaseId(targetCaseId);
    try {
      await testSuitesDeleteTestCase(suite_id, targetCaseId);
      toast.success("Test case deleted", "Deleted");
      if (case_id === targetCaseId) {
        setIsCreatingNewCase(false);
        setIsEditingCase(false);
        navigate(`/test-suites/${suite_id}`);
      }
      await fetchSuiteDetail(suite_id);
      await fetchSuites();
    } catch (err: any) {
      toast.error(err.message, "Failed to delete test case");
    } finally {
      setDeletingCaseId(null);
    }
  };

  const openRunModal = async () => {
    setIsRunModalOpen(true);
    try {
      const resp = await environmentSnapshotsListEnvironmentSnapshots({
        limit: 50,
      });
      const data = (resp as any)?.data ?? resp;
      if (data && "snapshots" in data) {
        const listResp = data as SnapshotListResponse;
        setSnapshots(listResp.snapshots);
        if (listResp.snapshots.length > 0) {
          setSelectedSnapshotId(listResp.snapshots[0].id);
        }
      }
    } catch {
      // fallback
    }
  };

  const handleTriggerRun = async () => {
    if (!suite_id || !selectedSnapshotId) return;
    setTriggeringRun(true);
    try {
      const resp = await testRunsCreateTestRun({
        suite_id: suite_id,
        environment_snapshot_id: selectedSnapshotId,
      });
      const data = (resp as any)?.data ?? resp;
      if (data && "id" in data) {
        const run = data as TestRunResponse;
        toast.success(
          `Run ${run.id.slice(0, 8)} started in background.`,
          "Test Run Queued",
        );
        setIsRunModalOpen(false);
        navigate(`/test-runs?test_run_id=${run.id}`);
      }
    } catch (err: any) {
      toast.error(err.message, "Failed to trigger run");
    } finally {
      setTriggeringRun(false);
    }
  };

  return (
    <div className="flex-1 flex h-full overflow-hidden bg-stone-50 dark:bg-stone-950">
      {/* 1. Left Suites Column */}
      <div className="w-72 lg:w-80 border-r border-stone-200 dark:border-stone-800 flex flex-col bg-white dark:bg-stone-900 shrink-0">
        <div className="p-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            <h1 className="font-semibold text-sm text-stone-900 dark:text-stone-100">
              Test Suites
            </h1>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => fetchSuites()}
              className="p-1.5 text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200 rounded-md hover:bg-stone-100 dark:hover:bg-stone-800"
              title="Refresh"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={() => setIsSuiteModalOpen(true)}
              className="flex items-center gap-1 text-xs font-medium px-2 py-1 bg-stone-900 hover:bg-stone-800 dark:bg-stone-100 dark:hover:bg-stone-200 text-white dark:text-stone-900 rounded-md transition"
            >
              <Plus className="w-3.5 h-3.5" />
              New Suite
            </button>
          </div>
        </div>

        {/* Agent Filter Bar & Archived Toggle */}
        <div className="px-3 py-2 border-b border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-900/50 space-y-2">
          <select
            value={agentFilter}
            onChange={(e) => {
              const val = e.target.value;
              setAgentFilter(val);
              fetchSuites(undefined, val);
            }}
            className="w-full text-xs bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-md px-2.5 py-1.5 text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="all">All Agents</option>
            {AGENT_OPTIONS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-stone-600 dark:text-stone-400 select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-stone-300 dark:border-stone-700 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
            />
            <span>Show archived suites</span>
          </label>
        </div>

        {/* Suites List */}
        <div className="flex-1 overflow-y-auto divide-y divide-stone-100 dark:divide-stone-800/60">
          {loading && suites.length === 0 ? (
            <div className="p-8 text-center text-xs text-stone-500">
              Loading suites...
            </div>
          ) : suites.length === 0 ? (
            <div className="p-8 text-center text-xs text-stone-500">
              No test suites found for the selected agent.
            </div>
          ) : (
            suites.map((s) => {
              const isSelected = suite_id === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => handleSelectSuite(s.id)}
                  className={`w-full text-left p-3.5 transition-colors flex items-start justify-between gap-2.5 ${
                    isSelected
                      ? "bg-indigo-50/60 dark:bg-indigo-950/20 border-l-2 border-indigo-600 dark:border-indigo-400"
                      : "hover:bg-stone-50 dark:hover:bg-stone-800/40"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-xs text-stone-900 dark:text-stone-100 truncate block">
                      {s.name}
                    </span>
                    {s.description && (
                      <p className="text-[11px] text-stone-500 dark:text-stone-400 line-clamp-1 mt-0.5">
                        {s.description}
                      </p>
                    )}
                    <div className="flex items-center gap-1.5 mt-2">
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${getAgentBadgeClass(
                          s.agent_type,
                        )}`}
                      >
                        {getAgentLabel(s.agent_type)}
                      </span>
                      <span className="text-[10px] text-stone-400">•</span>
                      <span className="text-[10px] text-stone-400">
                        {s.case_count} cases
                      </span>
                      {s.is_archived && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                          Archived
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight
                    className={`w-3.5 h-3.5 mt-0.5 text-stone-400 ${
                      isSelected ? "text-indigo-600 dark:text-indigo-400" : ""
                    }`}
                  />
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* 2. Middle Cases Column (within selected suite) */}
      <div className="w-80 lg:w-96 border-r border-stone-200 dark:border-stone-800 flex flex-col bg-stone-50/60 dark:bg-stone-900/40 shrink-0">
        <div className="p-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between bg-white dark:bg-stone-900">
          <div className="flex items-center gap-1.5 min-w-0">
            <MessageSquare className="w-4 h-4 text-indigo-500 shrink-0" />
            <h2 className="font-semibold text-xs text-stone-900 dark:text-stone-100 truncate">
              {suiteDetail ? suiteDetail.name : "Test Cases"}
            </h2>
            {suiteDetail && (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${getAgentBadgeClass(
                  suiteDetail.agent_type,
                )}`}
              >
                {getAgentLabel(suiteDetail.agent_type)}
              </span>
            )}
          </div>
          <button
            onClick={handleStartCreateCase}
            disabled={!suiteDetail}
            className="flex items-center gap-1 text-xs font-medium px-2 py-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition disabled:opacity-40 shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Case
          </button>
        </div>

        {/* Action Bar for Suite (Clone, Archive, Execute) */}
        {suiteDetail && (
          <div className="px-3.5 py-2 bg-stone-100/80 dark:bg-stone-900/90 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between text-xs gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-[11px] font-medium text-stone-500 dark:text-stone-400 whitespace-nowrap">
                {suiteDetail.test_cases.length} Multi-Turn Cases
              </span>
              {suiteDetail.is_archived && (
                <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 shrink-0">
                  Archived
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => handleToggleArchiveSuite(suiteDetail)}
                disabled={actionLoading}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-200/60 dark:hover:bg-stone-800 rounded transition disabled:opacity-50"
                title={suiteDetail.is_archived ? "Restore suite" : "Archive suite"}
              >
                {suiteDetail.is_archived ? (
                  <>
                    <ArchiveRestore className="w-3 h-3 text-stone-500 dark:text-stone-400" />
                    Restore
                  </>
                ) : (
                  <>
                    <Archive className="w-3 h-3 text-stone-500 dark:text-stone-400" />
                    Archive
                  </>
                )}
              </button>
              <button
                onClick={() =>
                  handleCloneSuite(suiteDetail.id, suiteDetail.name)
                }
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-200/60 dark:hover:bg-stone-800 rounded transition"
                title="Clone suite"
              >
                <Copy className="w-3 h-3 text-stone-500 dark:text-stone-400" />
                Clone
              </button>
              <button
                onClick={openRunModal}
                disabled={suiteDetail.test_cases.length === 0}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded transition shadow-2xs disabled:opacity-50 whitespace-nowrap"
              >
                <Play className="w-3 h-3" />
                Execute Run
              </button>
            </div>
          </div>
        )}

        {/* Cases List */}
        <div className="flex-1 overflow-y-auto divide-y divide-stone-100 dark:divide-stone-800">
          {!suiteDetail ? (
            <div className="p-8 text-center text-xs text-stone-400">
              Select a suite first.
            </div>
          ) : suiteDetail.test_cases.length === 0 && !isCreatingNewCase ? (
            <div className="p-8 text-center text-xs text-stone-500 space-y-2">
              <p>No test cases defined yet.</p>
              <button
                onClick={handleStartCreateCase}
                className="inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
              >
                <Plus className="w-3.5 h-3.5" />
                Create your first test scenario
              </button>
            </div>
          ) : (
            <>
              {isCreatingNewCase && (
                <div className="p-3 bg-indigo-50/80 dark:bg-indigo-950/40 border-l-2 border-indigo-600 dark:border-indigo-400">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono px-1 py-0.5 rounded bg-indigo-200/60 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 font-semibold">
                      NEW
                    </span>
                    <span className="text-xs font-semibold text-indigo-900 dark:text-indigo-200 truncate">
                      {caseTitle.trim() || "Untitled Scenario..."}
                    </span>
                  </div>
                  <p className="text-[10px] text-indigo-600/80 dark:text-indigo-400/80 mt-1">
                    Composing new scenario in detail view &rarr;
                  </p>
                </div>
              )}
              {suiteDetail.test_cases.map((c, idx) => {
                const isCaseSelected = !isCreatingNewCase && case_id === c.id;
                const turns = Array.isArray(c.script_turns)
                  ? c.script_turns
                  : [];
                return (
                  <div
                    key={c.id}
                    onClick={() => handleSelectCase(c.id)}
                    className={`p-3 transition-colors cursor-pointer flex flex-col gap-1.5 ${
                      isCaseSelected
                        ? "bg-white dark:bg-stone-900 border-l-2 border-indigo-600 dark:border-indigo-400 shadow-xs"
                        : "hover:bg-white/60 dark:hover:bg-stone-800/50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-[10px] font-mono px-1 py-0.2 rounded bg-stone-100 dark:bg-stone-800 text-stone-500">
                          #{idx + 1}
                        </span>
                        <span className="font-medium text-xs text-stone-900 dark:text-stone-100 truncate">
                          {c.title}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={(e) => handleDeleteCase(c.id, e)}
                          disabled={deletingCaseId === c.id}
                          className="p-1 text-stone-400 hover:text-rose-600 dark:hover:text-rose-400 rounded hover:bg-stone-100 dark:hover:bg-stone-800 transition disabled:opacity-50"
                          title="Delete test case"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    <div className="text-[11px] text-stone-500 dark:text-stone-400 line-clamp-2">
                      {turns.length > 0 ? (
                        <span>
                          <strong className="text-stone-700 dark:text-stone-300 font-medium">
                            {turns[0].role === "user"
                              ? "User: "
                              : "Assistant: "}
                          </strong>
                          {String(turns[0].content ?? "")}
                        </span>
                      ) : (
                        <span className="italic text-stone-400">
                          Empty script turns
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-[10px] text-stone-400 mt-0.5">
                      <span>{turns.length} turns</span>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      {/* 3. Right Test Case Detail & Integrated Thread View */}
      <div className="flex-1 flex flex-col h-full overflow-hidden bg-stone-50 dark:bg-stone-950 select-text selection:bg-indigo-200 dark:selection:bg-indigo-800 selection:text-stone-900 dark:selection:text-stone-100">
        {selectedCase || isCreatingNewCase ? (
          <div className="flex-1 flex flex-col h-full overflow-hidden select-text">
            {/* Header: Title / Actions */}
            <div className="px-6 py-4 bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between shrink-0 select-none">
              <div className="flex-1 min-w-0 mr-4 select-text">
                {isEditingCase || isCreatingNewCase ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Scenario Title / Inquiry Name (e.g. Check-in policy inquiry)"
                        value={caseTitle}
                        onChange={(e) => setCaseTitle(e.target.value)}
                        className="flex-1 px-3 py-1.5 text-base font-semibold bg-stone-50 dark:bg-stone-950 border border-stone-300 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 select-text cursor-text selection:bg-indigo-200 dark:selection:bg-indigo-800 selection:text-stone-900 dark:selection:text-stone-100"
                      />
                      <button
                        type="button"
                        onClick={handleSuggestTitle}
                        disabled={suggestingTitle || suggestingTurn}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/60 dark:hover:bg-indigo-900/60 rounded-lg border border-indigo-200 dark:border-indigo-800 transition cursor-pointer disabled:opacity-50 shrink-0"
                        title="Generate concise scenario title via LLM"
                      >
                        {suggestingTitle ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-500" />
                        ) : (
                          <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                        )}
                        <span>Suggest Title</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h2 className="text-base font-bold text-stone-900 dark:text-stone-100 break-words select-text cursor-text selection:bg-indigo-200 dark:selection:bg-indigo-800 selection:text-stone-900 dark:selection:text-stone-100">
                      {selectedCase?.title}
                    </h2>
                    <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5 select-none">
                      Multi-turn conversational scenario with {caseTurns.length}{" "}
                      turns
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {isEditingCase || isCreatingNewCase ? (
                  <>
                    <button
                      type="button"
                      onClick={handleCancelEditOrNew}
                      className="px-3 py-1.5 text-xs font-medium text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveCase}
                      disabled={savingCase || !caseTitle.trim()}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition shadow-xs disabled:opacity-50 cursor-pointer"
                    >
                      <Check className="w-3.5 h-3.5" />
                      {savingCase
                        ? "Saving..."
                        : isCreatingNewCase
                          ? "Save Test Case"
                          : "Apply Changes"}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setIsEditingCase(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 hover:bg-stone-200 dark:bg-stone-800 dark:hover:bg-stone-700 rounded-lg transition cursor-pointer"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      Edit Script
                    </button>
                    {selectedCase && (
                      <button
                        type="button"
                        onClick={(e) => handleDeleteCase(selectedCase.id, e)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition cursor-pointer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Conversation Stream - ThreadDetail Style */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 select-text selection:bg-indigo-200 dark:selection:bg-indigo-800 selection:text-stone-900 dark:selection:text-stone-100">
              <div className="max-w-4xl mx-auto space-y-6 select-text">
                {caseTurns.map((turn, idx) => {
                  const isUser = turn.role === "user";
                  const agentDisplayName = getAgentDisplayName(
                    suiteDetail?.agent_type,
                    getAgentLabel(suiteDetail?.agent_type),
                  );

                  return (
                    <div key={idx} className="w-full">
                      {isUser ? (
                        // User message on the RIGHT
                        <div className="flex flex-col items-end w-full space-y-1">
                          <div className="flex items-center gap-1.5 text-[11px] text-stone-500 pr-2 select-none">
                            <span className="font-medium text-stone-600 dark:text-stone-400 flex items-center gap-1">
                              <User className="w-3 h-3 text-stone-400" />
                              Human
                            </span>
                            <span>•</span>
                            <span className="font-mono text-[10px] text-stone-400">
                              Turn #{idx + 1}
                            </span>
                            {isEditingCase || isCreatingNewCase ? (
                              <div className="flex items-center gap-1 ml-1">
                                <button
                                  type="button"
                                  onClick={() => handleToggleTurnRole(idx)}
                                  className="inline-flex items-center gap-1 text-[10px] font-medium text-stone-400 hover:text-indigo-600 dark:hover:text-indigo-400 px-1.5 py-0.5 rounded hover:bg-stone-100 dark:hover:bg-stone-800 transition cursor-pointer"
                                  title={`Switch to ${agentDisplayName}`}
                                >
                                  <ArrowLeftRight className="w-2.5 h-2.5" />
                                  <span>Make {agentDisplayName}</span>
                                </button>
                                {caseTurns.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteTurn(idx)}
                                    className="text-stone-400 hover:text-rose-500 p-0.5 rounded hover:bg-rose-50 dark:hover:bg-rose-950/40 transition cursor-pointer"
                                    title="Delete turn"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleCopyTurn(idx, turn.content)}
                                className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 p-0.5 rounded hover:bg-stone-100 dark:hover:bg-stone-800 transition cursor-pointer ml-1"
                                title="Copy turn text"
                              >
                                {copiedTurnIdx === idx ? (
                                  <Check className="w-3 h-3 text-emerald-500" />
                                ) : (
                                  <Copy className="w-3 h-3" />
                                )}
                              </button>
                            )}
                          </div>

                          {isEditingCase || isCreatingNewCase ? (
                            <div
                              onClick={(e) => {
                                if (e.target === e.currentTarget) {
                                  e.currentTarget.querySelector("textarea")?.focus();
                                }
                              }}
                              className="max-w-2xl w-full ml-auto rounded-3xl p-3 bg-stone-200 dark:bg-[#282a2c] shadow-xs select-text cursor-text"
                            >
                              <AutoResizeTextarea
                                value={turn.content}
                                placeholder="Enter human / user utterance..."
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setCaseTurns((prev) =>
                                    prev.map((t, i) =>
                                      i === idx ? { ...t, content: val } : t,
                                    ),
                                  );
                                }}
                                className="w-full bg-transparent text-stone-900 dark:text-stone-100 text-sm leading-relaxed focus:outline-none select-text cursor-text selection:bg-indigo-200 dark:selection:bg-indigo-800 selection:text-stone-900 dark:selection:text-stone-100 px-2 py-1"
                              />
                            </div>
                          ) : (
                            <div className="max-w-2xl w-fit ml-auto rounded-3xl px-5 py-3.5 bg-stone-200 dark:bg-[#282a2c] text-stone-900 dark:text-stone-100 text-left text-sm leading-relaxed shadow-xs whitespace-pre-wrap select-text cursor-text selection:bg-indigo-200 dark:selection:bg-indigo-800 selection:text-stone-900 dark:selection:text-stone-100">
                              {turn.content || (
                                <span className="italic text-stone-400 select-none">
                                  Empty message
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        // Assistant message on the LEFT
                        <div className="flex flex-col items-start w-full space-y-1.5">
                          <div className="flex items-center justify-between gap-2 w-full text-xs select-none">
                            <div className="flex items-center gap-1.5 font-semibold text-stone-800 dark:text-stone-200">
                              <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300">
                                <Bot className="w-3 h-3" />
                              </div>
                              <span>{agentDisplayName}</span>
                              <span className="text-stone-400 dark:text-stone-500 font-normal">
                                •
                              </span>
                              <span className="font-mono text-[10px] text-stone-400 font-normal">
                                Turn #{idx + 1}
                              </span>
                            </div>

                            {isEditingCase || isCreatingNewCase ? (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleToggleTurnRole(idx)}
                                  className="inline-flex items-center gap-1 text-[10px] font-medium text-stone-400 hover:text-indigo-600 dark:hover:text-indigo-400 px-1.5 py-0.5 rounded hover:bg-stone-100 dark:hover:bg-stone-800 transition cursor-pointer"
                                  title="Switch to Human"
                                >
                                  <ArrowLeftRight className="w-2.5 h-2.5" />
                                  <span>Make Human</span>
                                </button>
                                {caseTurns.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteTurn(idx)}
                                    className="text-stone-400 hover:text-rose-500 p-0.5 rounded hover:bg-rose-50 dark:hover:bg-rose-950/40 transition cursor-pointer text-xs"
                                    title="Delete turn"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleCopyTurn(idx, turn.content)}
                                className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 p-0.5 rounded hover:bg-stone-100 dark:hover:bg-stone-800 transition cursor-pointer"
                                title="Copy turn text"
                              >
                                {copiedTurnIdx === idx ? (
                                  <Check className="w-3 h-3 text-emerald-500" />
                                ) : (
                                  <Copy className="w-3 h-3" />
                                )}
                              </button>
                            )}
                          </div>

                          {turn.generating ? (
                            <div className="w-full text-left pl-6 md:pl-7 select-none">
                              <div className="rounded-2xl p-4 bg-white dark:bg-stone-900 border border-indigo-200 dark:border-indigo-900/60 shadow-xs flex items-center gap-2.5 text-stone-600 dark:text-stone-300 text-xs">
                                <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-500" />
                                <span>Generating {agentDisplayName} suggestion...</span>
                              </div>
                            </div>
                          ) : isEditingCase || isCreatingNewCase ? (
                            <div className="w-full text-left pl-6 md:pl-7">
                              <div
                                onClick={(e) => {
                                  if (e.target === e.currentTarget) {
                                    e.currentTarget.querySelector("textarea")?.focus();
                                  }
                                }}
                                className="rounded-2xl p-3 bg-white dark:bg-stone-900 border border-stone-300 dark:border-stone-800 shadow-xs select-text cursor-text"
                              >
                                <AutoResizeTextarea
                                  value={turn.content}
                                  placeholder={`Enter ${agentDisplayName} script response...`}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setCaseTurns((prev) =>
                                      prev.map((t, i) =>
                                        i === idx ? { ...t, content: val } : t,
                                      ),
                                    );
                                  }}
                                  className="w-full bg-transparent text-stone-900 dark:text-[#e3e3e3] text-sm leading-relaxed focus:outline-none select-text cursor-text selection:bg-indigo-200 dark:selection:bg-indigo-800 selection:text-stone-900 dark:selection:text-stone-100 px-2 py-1"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="w-full text-left text-sm leading-relaxed text-stone-900 dark:text-[#e3e3e3] pl-6 md:pl-7 whitespace-pre-wrap select-text cursor-text selection:bg-indigo-200 dark:selection:bg-indigo-800 selection:text-stone-900 dark:selection:text-stone-100">
                              {turn.content || (
                                <span className="italic text-stone-400 select-none">
                                  Empty message
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Action buttons to append turns or auto-suggest */}
                {(isEditingCase || isCreatingNewCase) && (
                  <div className="pt-2 flex items-center justify-center gap-2.5">
                    <button
                      type="button"
                      onClick={() => handleAddMessageTurn("user")}
                      disabled={suggestingTurn}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 bg-stone-100 hover:bg-stone-200 dark:bg-stone-800 dark:hover:bg-stone-700 rounded-lg transition cursor-pointer disabled:opacity-50"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Human</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAddMessageTurn("assistant")}
                      disabled={suggestingTurn}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 bg-stone-100 hover:bg-stone-200 dark:bg-stone-800 dark:hover:bg-stone-700 rounded-lg transition cursor-pointer disabled:opacity-50"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>
                        {getAgentDisplayName(
                          suiteDetail?.agent_type,
                          getAgentLabel(suiteDetail?.agent_type),
                        )}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={handleAutoSuggest}
                      disabled={suggestingTurn}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded-lg border border-dashed border-indigo-300 dark:border-indigo-800/80 transition cursor-pointer disabled:opacity-50"
                    >
                      {suggestingTurn ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-500" />
                      ) : (
                        <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                      )}
                      <span>Auto Suggest</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-3">
            <MessageSquare className="w-10 h-10 text-stone-300 dark:text-stone-700" />
            <h3 className="text-sm font-semibold text-stone-700 dark:text-stone-300">
              No Test Case Selected
            </h3>
            <p className="text-xs text-stone-400 max-w-sm">
              Select a test case from the list on the left or create a new
              multi-turn conversational scenario.
            </p>
            {suiteDetail && (
              <button
                onClick={handleStartCreateCase}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition shadow-xs"
              >
                <Plus className="w-4 h-4" />
                Add New Scenario
              </button>
            )}
          </div>
        )}
      </div>

      {/* New Suite Modal */}
      {isSuiteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-stone-900 rounded-xl max-w-md w-full p-6 shadow-xl border border-stone-200 dark:border-stone-800 space-y-4">
            <h3 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
              Create Test Suite
            </h3>
            <form onSubmit={handleCreateSuite} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-stone-700 dark:text-stone-300 mb-1">
                  Target Agent *
                </label>
                <select
                  value={suiteAgentType}
                  onChange={(e) => setSuiteAgentType(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {AGENT_OPTIONS.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-stone-500 mt-1">
                  Test scenarios will simulate interactions and evaluate
                  directives specifically for this agent.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-700 dark:text-stone-300 mb-1">
                  Suite Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Sales Concierge Core Inquiries"
                  value={suiteName}
                  onChange={(e) => setSuiteName(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-700 dark:text-stone-300 mb-1">
                  Description
                </label>
                <AutoResizeTextarea
                  rows={2}
                  placeholder="Scenarios covered in this suite"
                  value={suiteDesc}
                  onChange={(e) => setSuiteDesc(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsSuiteModalOpen(false)}
                  className="px-4 py-2 text-xs font-medium text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingSuite || !suiteName.trim()}
                  className="px-4 py-2 text-xs font-medium bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition disabled:opacity-50"
                >
                  {creatingSuite ? "Creating..." : "Create Suite"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Trigger Test Run Modal */}
      {isRunModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-stone-900 rounded-xl max-w-md w-full p-6 shadow-xl border border-stone-200 dark:border-stone-800 space-y-4">
            <h3 className="text-lg font-semibold text-stone-900 dark:text-stone-100 flex items-center gap-2">
              <Play className="w-5 h-5 text-indigo-600" />
              Execute Test Run
            </h3>
            <p className="text-xs text-stone-500">
              Select an environment snapshot to pair with suite{" "}
              <strong>'{suiteDetail?.name}'</strong>. Isolated test threads will
              execute and record actual generated outputs for each test case.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-stone-700 dark:text-stone-300 mb-1">
                  Target Environment Baseline *
                </label>
                {snapshots.length === 0 ? (
                  <div className="text-xs text-amber-600 p-2 bg-amber-50 dark:bg-amber-950/30 rounded border border-amber-200 dark:border-amber-800">
                    No frozen environment snapshots found. Please freeze an
                    environment baseline first in Snapshots.
                  </div>
                ) : (
                  <select
                    value={selectedSnapshotId}
                    onChange={(e) => setSelectedSnapshotId(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 rounded-lg text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {snapshots.map((snap) => (
                      <option key={snap.id} value={snap.id}>
                        {snap.name} ({snap.rule_version_ids.length} rules,{" "}
                        {snap.knowledge_version_ids.length} items)
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsRunModalOpen(false)}
                  className="px-4 py-2 text-xs font-medium text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleTriggerRun}
                  disabled={triggeringRun || !selectedSnapshotId}
                  className="px-4 py-2 text-xs font-medium bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition disabled:opacity-50"
                >
                  {triggeringRun ? "Starting..." : "Start Test Run"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
